"use client";

import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Clock, Tag, Eye, X } from "lucide-react";
import { useRouter } from "next/navigation";
import dayjs from "dayjs";
import PromotionDetail from "@/app/dashboard/services-request/components/promotion-detail";
import { startSalesFeedback } from "@/app/dashboard/services-request/lib/actions";

const POPUP_SUPPRESS_HOURS = 12;
const POPUP_KEY = "promo_popup_suppressed_until";

interface Promotion {
  id: string;
  title: string;
  description: string;
  tag: string;
  duration: Date | string;
  image: string;
}

export default function PromoPopup({
  promotions,
}: {
  promotions: Promotion[];
}) {
  const [visible, setVisible] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const router = useRouter();

  const promo = promotions[0] ?? null;

  useEffect(() => {
    if (!promo) return;
    const until = localStorage.getItem(POPUP_KEY);
    const suppressed = until && Date.now() < Number(until);
    if (!suppressed) {
      const t = setTimeout(() => setVisible(true), 800);
      return () => clearTimeout(t);
    }
  }, [promo?.id]);

  const dismiss = () => {
    localStorage.setItem(
      POPUP_KEY,
      String(Date.now() + POPUP_SUPPRESS_HOURS * 60 * 60 * 1000),
    );
    setVisible(false);
  };

  const handleStartConversation = async (title: string) => {
    const res = await startSalesFeedback(
      title,
      `Hi, I'm interested in the "${title}" promotion.`,
    );
    if (res?.feedbackId) {
      router.push(`/dashboard/messages/${res.feedbackId}`);
    }
    setDetailOpen(false);
    dismiss();
  };

  if (!promo) return null;

  const isNew = promo.tag === "New";
  const imageUrl = promo.image.startsWith("http")
    ? promo.image
    : `https://res.cloudinary.com/${process.env.NEXT_PUBLIC_CLOUDINARY_CLOUD_NAME}/image/upload/${promo.image}`;

  return (
    <>
      <AnimatePresence>
        {visible && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 z-50 bg-black/40 backdrop-blur-[2px]"
              onClick={dismiss}
            />

            {/* Card */}
            <motion.div
              initial={{ opacity: 0, scale: 0.88, y: 24 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.88, y: 24 }}
              transition={{ type: "spring", stiffness: 300, damping: 26 }}
              className="fixed z-50 inset-x-4 bottom-6 sm:inset-auto sm:bottom-8 sm:right-8 sm:w-[320px] rounded-[20px] overflow-hidden shadow-2xl border border-border bg-white"
            >
              {/* Image header */}
              <div
                className="h-[160px] w-full bg-cover bg-center relative"
                style={{ backgroundImage: `url('${imageUrl}')` }}
              >
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />

                <span
                  className={`absolute top-3 left-3 text-[10px] font-semibold px-2.5 py-1 rounded-full flex items-center gap-1 ${
                    isNew ? "bg-primary text-white" : "bg-amber-400 text-amber-900"
                  }`}
                >
                  {isNew ? <Tag size={10} /> : <Clock size={10} />}
                  {promo.tag}
                </span>

                <button
                  onClick={dismiss}
                  className="absolute top-3 right-3 w-7 h-7 rounded-full bg-black/40 backdrop-blur-sm flex items-center justify-center text-white hover:bg-black/60 transition-colors"
                >
                  <X size={13} />
                </button>

                <div className="absolute bottom-0 left-0 right-0 px-4 pb-3">
                  <p className="text-white text-xs font-medium opacity-80">
                    Special offer for you
                  </p>
                </div>
              </div>

              {/* Content */}
              <div className="p-4 flex flex-col gap-2.5">
                <p className="font-semibold text-base text-foreground leading-snug">
                  {promo.title}
                </p>
                <p className="text-xs text-muted-foreground leading-relaxed line-clamp-3">
                  {promo.description}
                </p>
                <div className="flex items-center gap-1 text-[10px] text-muted-foreground">
                  <Clock size={10} />
                  <span>Valid until {dayjs(promo.duration).format("MMM D, YYYY")}</span>
                </div>

                <div className="flex gap-2 mt-1">
                  <button
                    onClick={() => setDetailOpen(true)}
                    className="flex-1 flex items-center justify-center gap-1.5 bg-primary text-white text-xs font-medium py-2.5 rounded-[10px] hover:opacity-90 transition-opacity"
                  >
                    <Eye size={12} />
                    View Offer
                  </button>
                  <button
                    onClick={dismiss}
                    className="px-4 text-xs text-muted-foreground border border-border rounded-[10px] hover:bg-accent transition-colors"
                  >
                    Later
                  </button>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      <PromotionDetail
        promo={promo}
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        onStartConversation={handleStartConversation}
      />
    </>
  );
}
